import { useQueryGetAllBots } from "@apis:bot/bot";
import { useQueryGetAllOrganizations } from "@apis:organization/organization";
import { SearchableSelect } from "@components:SearchableSelect/SearchableSelect";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { useBotSelection } from "@hooks:useBotSelection";
import { useNewChat } from "@hooks:useNewChat";
import { useSearchActions } from "@stores:app/talk/search";
import { useMemo } from "react";
import { useLocation } from "react-router";

import { AppType } from "@:constants";

interface BotSelectionProps {
  value?: string | number; // botId
  onChange?: (botId: string) => void;
  noMatchText?: string;
  app?: AppType;
}

export const BotSelection = (props: BotSelectionProps) => {
  const { pathname } = useLocation();

  const searchActions = useSearchActions();
  const { newChat } = useNewChat();

  const isChatPage = pathname.startsWith("/app/chat");
  const isSearchPage = pathname.startsWith("/app/search");

  const listAllBotsQuery = useQueryGetAllBots(true, props.app);
  const listAllOrganizations = useQueryGetAllOrganizations();

  const combinedBotAndOrganizations = useMemo(() => {
    return listAllBotsQuery.data?.map((bot) => {
      const organization = listAllOrganizations.data?.find(
        (org) => org.id === bot.organization_id,
      );
      return {
        ...bot,
        organizationName: organization?.name,
      };
    });
  }, [listAllBotsQuery?.data, listAllOrganizations?.data]);

  const canGroupByOrganization = useMemo(() => {
    if (!listAllBotsQuery.data) return false;

    const uniqueOrganizationIds = new Set(
      listAllBotsQuery.data.map((bot) => bot.organization_id),
    );

    return uniqueOrganizationIds.size >= 1;
  }, [listAllBotsQuery.data]);

  const current = combinedBotAndOrganizations?.find(
    (bot) => bot.id === props.value,
  );

  const sortedBots = useMemo(() => {
    return (
      combinedBotAndOrganizations?.sort((a, b) => {
        return (
          a.organizationName?.localeCompare(b.organizationName as string) ?? 0
        );
      }) ?? []
    );
  }, [combinedBotAndOrganizations]);

  const isLoading = listAllBotsQuery?.isLoading || listAllBotsQuery?.isLoading;

  return (
    <SearchableSelect
      options={sortedBots}
      getOptionLabel={(option) => option?.name as string}
      getOptionValue={(option) => option?.id as string}
      selectedOption={current}
      groupBy={
        canGroupByOrganization
          ? (option) => option?.organizationName as string
          : undefined
      }
      SearchProps={{
        placeholder: canGroupByOrganization
          ? "Bot name"
          : "Bot or customer name...",
      }}
      RenderValueProps={{
        startAdornment: (
          <SVGInternalIcon icon="botIcon" sx={{ width: 22, height: 22 }} />
        ),
      }}
      onChange={async (botId) => {
        if (isChatPage) {
          newChat();
        }

        if (isSearchPage) {
          searchActions.reset();
        }

        props?.onChange?.(botId);
      }}
      isLoading={isLoading}
      loadingText="Loading bots..."
      filterOptions={(option, searchText) => {
        return (
          option?.name?.toLowerCase()?.includes(searchText.toLowerCase()) ||
          option?.organizationName
            ?.toLowerCase()
            ?.includes(searchText.toLowerCase()) ||
          false
        );
      }}
      noMatchText={props?.noMatchText}
    />
  );
};

interface BotSelectionPersistentProps {
  app?: AppType;
}

export const BotSelectionPersistent = ({
  app,
}: BotSelectionPersistentProps) => {
  const { selectBotId, getSelectedBot } = useBotSelection();
  const selectedBot = getSelectedBot();

  return (
    <BotSelection
      value={selectedBot?.id}
      onChange={selectBotId}
      noMatchText="No bots available"
      app={app}
    />
  );
};
